import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { DigestResolutionStats } from "@/types/activity";
import { ConversationDetails } from "@/types/conversation/conversation";
import { formatDuration } from "@/utils/formatting/general";
import { buildPulseAggregates, buildResolutionStats } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface InsightsPulseStripProps {
  conversations: ConversationDetails[];
  dateRange: string;
  isLoading?: boolean;
  /** AI digest's resolution stats — preferred when present, since it also covers conversations not yet analyzed for resolution. */
  digestResolution?: DigestResolutionStats;
  /** True while the digest call is in flight and hasn't returned classification stats yet — shows a skeleton on just the Resolution card instead of the (possibly incomplete) client aggregate. */
  resolutionLoading?: boolean;
}

const DATE_RANGE_LABEL: Record<string, string> = {
  "7d": "last 7d",
  "30d": "last 30d",
  "90d": "last 90d",
  all: "all time",
};

export const InsightsPulseStrip = ({
  conversations,
  dateRange,
  isLoading,
  digestResolution,
  resolutionLoading,
}: InsightsPulseStripProps) => {
  const stats = buildPulseAggregates(conversations, dateRange);
  const resolution = digestResolution ?? buildResolutionStats(conversations);
  const rangeLabel = DATE_RANGE_LABEL[dateRange] ?? "last 30d";

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-2xl border border-gray-200 p-5 h-[120px] animate-pulse"
          >
            <div className="h-3 w-24 bg-gray-200 rounded mb-3" />
            <div className="h-6 w-16 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const positivePct =
    stats.sentimentMix.total > 0
      ? Math.round((stats.sentimentMix.positive / stats.sentimentMix.total) * 100)
      : 0;
  const negativePct =
    stats.sentimentMix.total > 0
      ? Math.round((stats.sentimentMix.negative / stats.sentimentMix.total) * 100)
      : 0;
  const neutralPct = Math.max(0, 100 - positivePct - negativePct);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {/* Volume + delta */}
      <PulseCard
        genieIcon="conversation"
        label="Volume"
        sublabel={rangeLabel}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
            {stats.thisPeriodCount || stats.total}
          </span>
          <DeltaPill value={stats.volumeDelta} />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          vs {stats.priorPeriodCount} prior period
        </p>
      </PulseCard>

      {/* Sentiment mix */}
      <PulseCard genieIcon="smile" label="Sentiment mix">
        {stats.sentimentMix.total > 0 ? (
          <>
            <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 mb-2">
              <div className="bg-[#4E9CFF]" style={{ width: `${positivePct}%` }} />
              <div className="bg-gray-300" style={{ width: `${neutralPct}%` }} />
              <div className="bg-[#FF4DB8]" style={{ width: `${negativePct}%` }} />
            </div>
            <div className="flex items-center justify-between gap-2 text-[11px] text-gray-600">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4E9CFF]" />
                <span className="font-semibold text-gray-900">{positivePct}%</span>
                <span className="text-gray-500">positive</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                <span className="font-semibold text-gray-900">{neutralPct}%</span>
                <span className="text-gray-500">neutral</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF4DB8]" />
                <span className="font-semibold text-gray-900">{negativePct}%</span>
                <span className="text-gray-500">negative</span>
              </span>
            </div>
            {stats.satisfactionAvg !== null && (
              <p className="text-xs text-gray-500 mt-2">
                Avg satisfaction{" "}
                <span className="font-semibold text-gray-900">{stats.satisfactionAvg}%</span>
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">No analysis yet</p>
        )}
      </PulseCard>

      {/* Avg duration + talk balance */}
      <PulseCard genieIcon="microphone" label="Avg duration">
        <div className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
          {stats.avgDurationSeconds !== null
            ? formatDuration(stats.avgDurationSeconds)
            : "—"}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {stats.agentTalkAvg !== null ? (
            <>
              Genie talks{" "}
              <span className="font-semibold text-gray-900">{stats.agentTalkAvg}%</span> on average
            </>
          ) : (
            "Talk-time analysis pending"
          )}
        </p>
      </PulseCard>

      {/* Resolution rate */}
      <PulseCard genieIcon="smile" label="Resolution rate">
        {resolutionLoading ? (
          <div className="space-y-2">
            <div className="h-6 w-16 bg-gray-100 rounded animate-pulse" />
            <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
        ) : resolution.classified > 0 ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
                {resolution.resolutionRate}%
              </span>
              <span className="text-xs text-gray-500">resolved</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {resolution.resolved} resolved · {resolution.partial} partial · {resolution.unresolved} unresolved
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">No analysis yet</p>
        )}
      </PulseCard>

      {/* Escalations */}
      <PulseCard genieIcon="phone" label="Escalations">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
            {stats.escalationCount}
          </span>
          {stats.total > 0 && (
            <span className="text-xs text-gray-500">
              {Math.round((stats.escalationCount / stats.total) * 100)}% of calls
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {stats.escalationCount === 0
            ? "Nothing flagged for handoff"
            : "Calls routed to a human"}
        </p>
      </PulseCard>
    </div>
  );
};

const PulseCard = ({
  genieIcon,
  label,
  sublabel,
  children,
}: {
  genieIcon: GenieIconName;
  label: string;
  sublabel?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#4E9CFF]/30 transition-colors">
    <div className="flex items-center gap-2 mb-3">
      <GenieIcon name={genieIcon} size={22} />
      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        {label}
      </span>
      {sublabel && <span className="text-[10px] text-gray-400 ml-auto">{sublabel}</span>}
    </div>
    {children}
  </div>
);

const DeltaPill = ({ value }: { value: number }) => {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
        <Minus className="w-3 h-3" />
        flat
      </span>
    );
  }
  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#4E9CFF]/10 text-[#4E9CFF]">
        <ArrowUpRight className="w-3 h-3" />
        {value}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FF4DB8]/10 text-[#FF4DB8]">
      <ArrowDownRight className="w-3 h-3" />
      {Math.abs(value)}%
    </span>
  );
};
